import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/Brand";
import { findCity } from "@/config/site";
import { getMember, saveMember } from "@/services/demoMember";
import { getSignupStatus, isMember, frDate, resolveSession } from "@/services/signup";

const MAX_TRIES = 20;

const useSignupStatus = (id, sessionId) => {
  const [state, setState] = useState({ loading: !!id, data: null, tries: 0 });
  useEffect(() => {
    if (!id) return undefined;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const data = await getSignupStatus(id, sessionId).catch(() => null);
      if (stop) return;
      const done = data && (isMember(data.status) || data.status === "cancelled");
      setState({ loading: !done && tries < MAX_TRIES, data, tries });
      if (!done && tries < MAX_TRIES) setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; };
  }, [id, sessionId]);
  return state;
};

const Body = ({ loading, data }) => {
  if (data && isMember(data.status)) {
    const city = findCity(data.city);
    return (
      <>
        <h1 className="font-display text-4xl text-ivory sm:text-5xl">Bienvenue au <span className="text-gold-gradient">club</span></h1>
        <p data-testid="confirm-status" className="mt-5 text-base text-ivory/80">
          Votre inscription est confirmée{data.trial_end ? `. Essai gratuit jusqu'au ${frDate(data.trial_end)}` : ""}.
        </p>
        <p className="mt-2 text-sm text-ivory/60">Offres au départ de {city ? city.name : data.city}{data.email ? `, envoyées à ${data.email}` : ""}.</p>
        <Link to="/mon-compte" data-testid="confirm-dashboard-link" className="btn-gold group mt-8 justify-center px-8 py-4 text-base">
          Voir mon compte <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </>
    );
  }
  return (
    <>
      <h1 className="font-display text-4xl text-ivory sm:text-5xl">Inscription en cours</h1>
      <p data-testid="confirm-status" className="mt-5 text-base text-ivory/80">
        {loading ? "Nous attendons la confirmation de votre paiement…" : "Nous n'avons pas encore reçu la confirmation de votre paiement."}
      </p>
      {!loading && (
        <p className="mt-2 text-sm text-ivory/60">
          Si vous avez finalisé le paiement, la confirmation peut prendre quelques minutes. Sinon,{" "}
          <Link to="/abonnement" data-testid="confirm-retry-link" className="underline underline-offset-2">reprenez votre inscription</Link>.
        </p>
      )}
    </>
  );
};

export default function SignupConfirmation() {
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const [signupId, setSignupId] = useState(getMember().signupId);
  useEffect(() => {
    if (signupId || !sessionId) return;
    resolveSession(sessionId).then((id) => { saveMember({ signupId: id }); setSignupId(id); }).catch(() => {});
  }, [signupId, sessionId]);
  const state = useSignupStatus(signupId, sessionId);
  return (
    <div className="min-h-screen bg-ivory">
      <header className="flex justify-center px-5 pb-4 pt-8 sm:pt-12"><Logo tone="dark" /></header>
      <main className="mx-auto max-w-xl px-4 pb-16 sm:px-6">
        <motion.section
          data-testid="signup-confirmation"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="rounded-[32px] bg-navy px-5 py-12 text-center shadow-[0_30px_60px_-30px_rgba(11,21,48,0.7)] sm:px-10"
        >
          <Body loading={state.loading} data={state.data} />
        </motion.section>
      </main>
    </div>
  );
}
